import { handleAxiosError } from '@/api/helpers';
import {
  useBenchmarks,
  useSaveBenchmark,
  useUpdateBenchmark,
} from '@/hooks/query/benchmarks.hooks';
import { useToast } from '@/hooks/useToast';
import {
  EditableDropdown,
  EditableDropdownItem,
} from '@/ui/editable-dropdown/EditableDropdown';
import { AxiosError } from 'axios';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

type BenchmarksSelectInputProps = {
  value?: string;
  onChange: (item: EditableDropdownItem | null) => void;
  placeholder?: string;
};

export const BenchmarksSelectInput: React.FC<BenchmarksSelectInputProps> = ({
  value,
  onChange,
  placeholder,
}) => {
  const { t } = useTranslation();
  const toast = useToast();

  const { benchmarks, isLoading: isLoadingBenchmarks } = useBenchmarks({
    sort: [['created', 'asc'].join(',')],
    take: 1000,
  });

  const [benchmarkOptions, setBenchmarkOptions] = useState(
    benchmarks?.result || [],
  );

  useEffect(() => {
    if (benchmarks?.result) {
      setBenchmarkOptions(benchmarks.result);
    }
  }, [benchmarks?.result]);

  const saveBenchmarkMutation = useSaveBenchmark();
  const updateBenchmarkMutation = useUpdateBenchmark();

  const handleCreateNew = async (name: string) => {
    try {
      const newBenchmark = await saveBenchmarkMutation.create({ name });
      const newItem = {
        id: newBenchmark.id,
        name: newBenchmark.name,
      };

      setBenchmarkOptions((prev) => [...prev, newItem]);

      toast?.success(
        t('toast.success'),
        t('campaign.template.benchmark.created'),
      );
    } catch (e) {
      handleAxiosError(e as Error | AxiosError, toast);
    }
  };

  const handleEditName = async (
    item: EditableDropdownItem,
    newName: string,
  ) => {
    try {
      await updateBenchmarkMutation.update({
        benchmarkId: item.id,
        updates: { name: newName },
      });

      setBenchmarkOptions((prev) =>
        prev.map((benchmark) =>
          benchmark.id === item.id
            ? { ...benchmark, name: newName }
            : benchmark,
        ),
      );

      toast?.success(
        t('toast.success'),
        t('campaign.template.benchmark.updated'),
      );
    } catch (e) {
      handleAxiosError(e as Error | AxiosError, toast);
    }
  };

  return (
    <EditableDropdown
      value={value}
      options={benchmarkOptions}
      onChange={onChange}
      onCreateNew={handleCreateNew}
      onEditName={handleEditName}
      placeholder={placeholder}
      isLoading={isLoadingBenchmarks}
      createButtonText={t('campaign.template.benchmark.createNew')}
    />
  );
};
